<script setup lang="ts">
  import { computed, ref, onBeforeUnmount, watch } from 'vue';
  import { useBreakpoints, useScrollWidth, useScrollDetector, useCustomerOrderAbility, useCustomerAccepted } from '@storefront/storefront';
  import type { Unit } from '@othercommerce/domain';

  interface Variant {
    id: number,
    productId: number,
    title: string,
    photo: string | null,
    stockUnit: Unit,
    stockUnitBase: number,
    priceNet: number,
    priceGross: number,
    regularNet: number,
    regularGross: number,
  }

  interface Props {
    variant: Variant,
    wrapper?: HTMLElement | null,
  }

  const props = defineProps<Props>();

  const observer = ref<IntersectionObserver>();
  const wrapperIsNotIntersecting = ref(false);
  const visible = ref(false);

  const canCustomerOrder = useCustomerOrderAbility();
  const isCustomerAccepted = useCustomerAccepted();
  const breakpoints = useBreakpoints();
  const scroll = useScrollWidth();

  const width = computed(() => {
    return scroll.value + 'px';
  });

  const classes = computed(() => {
    return {
      'product-banner--visible': visible.value,
    };
  });

  useScrollDetector({
    threshold: 8,
    callback: ({ down }) => {
      if (down) {
        if (wrapperIsNotIntersecting.value) {
          visible.value = true;
        }
      } else {
        visible.value = false;
      }
    },
  });

  watch(() => props.wrapper, (wrapper) => {
    if (observer.value) {
      observer.value.disconnect();
    }

    if (!wrapper) {
      return;
    }

    observer.value = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        wrapperIsNotIntersecting.value = !entry.isIntersecting;
      });
    }, {
      root: document.body,
      threshold: 0.25,
    });

    observer.value.observe(wrapper);
  });

  onBeforeUnmount(() => {
    if (observer.value) {
      observer.value.disconnect();
    }
  });
</script>
<template>
  <div class="product-banner" :class="classes" :aria-hidden="!visible">
    <div class="container product-banner__container" :inert="!visible">
      <div class="product-banner__info">
        <MediaPicture :photo="variant.photo" class="product-banner__image" sizes="100x100 2x, 50x50" />
        <div class="product-banner__title" v-html="variant.title" />
      </div>
      <div v-if="canCustomerOrder" class="product-banner__price">
        <SfProductPrice current :price="variant" />
        <SfButton :href="$route('web.product.picker.create', { product: variant.productId, variant: variant.id })" :rounded="!breakpoints.lg" :square="!breakpoints.lg">
          <template #before>
            <SfIconCartAdd aria-hidden="true" />
          </template>
          <template v-if="breakpoints.lg" #default>
            {{ $t('SHOP.PRODUCT.ACTION.ADD_TO_CART') }}
          </template>
        </SfButton>
      </div>
      <div v-else>
        <div class="product-banner__price__guest">
          <RouterLink v-if="isCustomerAccepted === undefined" :href="$route('web.account.session.create')" rel="nofollow">
            {{ $t('SHOP.COMMON.MESSAGE.SIGN_IN_TO_SEE_PRICES_NOTICE') }}
          </RouterLink>
          <span v-else>
            {{ $t('SHOP.COMMON.MESSAGE.ACCOUNT_INACTIVE_NOTICE') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss">
  :root {
    --product-banner-top: 108px;
    --product-banner-box-shadow: var(--secondary-box-shadow-100);
    --product-banner-background: var(--color-white);
    --product-banner-backdrop: none;
    --product-banner-container-padding: 0.5rem 0;

    @media(max-width: 992px) {
      --product-banner-container-padding: 0.5rem;
      --product-banner-top: 65px;
    }
  }
</style>
<style scoped lang="scss">
  .product-banner {
    --space: v-bind(width);

    position: fixed;
    top: var(--product-banner-top);
    left: 0;
    right: var(--space);
    z-index: 95;
    transform: translateY(-200%);
    background: var(--product-banner-background);
    backdrop-filter: blur(var(--product-banner-backdrop));
    transition: var(--primary-transition);

    &--visible {
      box-shadow: var(--product-banner-box-shadow);
      transform: translateY(0);
    }

    &__container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 2rem;
      padding: var(--product-banner-container-padding);
    }

    &__info {
      display: flex;
      flex: 1 1 auto;
      align-items: center;
      gap: 1.25rem;
    }

    &__price {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      gap: 1.25rem;
      justify-content: flex-end;

      &__guest {
        color: var(--secondary-color);
        font-size: 0.75rem;
      }
    }

    :deep(.product-banner__image) {
      display: flex;

      img {
        width: 50px;
        aspect-ratio: 1 / 1;
      }
    }

    &__title {
      display: -webkit-box;
      line-clamp: 2;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
      overflow: hidden;
      font-size: 1.125rem;
      font-weight: var(--font-weight-semi);
    }
  }

  @media (max-width: 992px) {
    .product-banner {
      --sf-product-price-current-font-size: 0.875rem;
      --sf-product-price-per-font-size: 0.625rem;

      &__title {
        font-size: 1rem;
      }
    }
  }

  @media (max-width: 575px) {
    .product-banner {
      --sf-product-price-current-font-size: 0.75rem;
      --sf-product-price-per-font-size: 0.625rem;

      &__container, &__info, &__price {
        gap: 0.625rem;
      }

      :deep(.product-banner__image) {
        img {
          width: 46px;
          aspect-ratio: 1 / 1;
        }
      }

      &__title {
        font-size: 0.625rem;
      }
    }
  }
</style>
